// Utilities to auto-generate the next SKU based on existing products.
// Pattern: ANY letter/prefix + digits at the end (e.g. SKU041, PRD-007, DZi123).
// We take the numeric suffix, find the max, and increment. Padding is preserved.

const SKU_PATTERN = /^(.*?)(\d+)$/;

const DEFAULT_PREFIX = "SKU";
const DEFAULT_PAD = 3;

/**
 * Scan the products list, group by "prefix" (everything before the trailing digits),
 * and return the most common prefix + its next numeric suffix (padded to match).
 * Falls back to "SKU001" when no numeric SKU exists yet.
 */
export function inferSkuPattern(products = []) {
    const buckets = new Map(); // prefix -> { count, maxNum, pad }
    for (const p of products || []) {
        const s = (p?.sku || "").trim();
        const m = s.match(SKU_PATTERN);
        if (!m) continue;
        const [, prefix, digits] = m;
        const num = parseInt(digits, 10);
        if (Number.isNaN(num)) continue;
        const cur = buckets.get(prefix) || { count: 0, maxNum: 0, pad: digits.length };
        cur.count += 1;
        if (num > cur.maxNum) cur.maxNum = num;
        cur.pad = Math.max(cur.pad, digits.length);
        buckets.set(prefix, cur);
    }
    if (buckets.size === 0) {
        return { prefix: DEFAULT_PREFIX, nextNum: 1, pad: DEFAULT_PAD };
    }
    // Winner: highest count, then highest maxNum
    let best = null;
    for (const [prefix, info] of buckets.entries()) {
        if (!best) { best = { prefix, ...info }; continue; }
        if (info.count > best.count || (info.count === best.count && info.maxNum > best.maxNum)) {
            best = { prefix, ...info };
        }
    }
    return { prefix: best.prefix, nextNum: best.maxNum + 1, pad: best.pad };
}

const format = (prefix, num, pad) => `${prefix}${String(num).padStart(pad, "0")}`;

/** Get the next single SKU (skipping any values already in `usedSkus` set). */
export function getNextSku(products = [], usedSkus = new Set()) {
    const { prefix, nextNum, pad } = inferSkuPattern(products);
    let n = nextNum;
    while (usedSkus.has(format(prefix, n, pad))) n += 1;
    return format(prefix, n, pad);
}

/**
 * Get N sequential next SKUs (each not colliding with existing products or with
 * SKUs already returned in this call). Useful for the bulk-add dialog.
 */
export function getNextSkus(products = [], count = 1, extraUsed = []) {
    const { prefix, nextNum, pad } = inferSkuPattern(products);
    const used = new Set(extraUsed);
    const out = [];
    let n = nextNum;
    while (out.length < count) {
        const s = format(prefix, n, pad);
        if (!used.has(s)) {
            out.push(s);
            used.add(s);
        }
        n += 1;
    }
    return out;
}
